import React, { useEffect, useState } from "react";
import { clearSession } from "../shared/_helpers/session";
import { uploadDocument, removeDocument, getDocuments, checkBlockedStatus } from "../shared/_services/common.services";
import { CLIENT_ACCESS_DENIED_MESSAGE, CLIENT_ACCESS_DENIED_REDIRECT, isForbiddenClientAccess } from "../shared/_helpers/apiErrors";
import moment from 'moment';
import { useParams, useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import Loader from "./Loader";
import DashboardUserHeader from "../components/dashboard-flow/DashboardUserHeader";
import StarsIcon from "../styles/StarsIcon";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogDescription,
  DialogTitle,
  Page,
  PageHead,
  TableWrap,
  Table,
  THead,
  TBody,
  Tr,
  Th,
  Td,
} from "./ui";
import documentPlaceholder from "../assets/images/No Documents.png"
import AgreementsSection from "./onboarding/AgreementsSection";

// SEC-037: file_url is server-supplied and rendered as an href. A plain <a>
// closes the react-router open-redirect path, but an href isn't safe just
// because it isn't a router `to` — a `javascript:` URI would execute on
// click regardless of which element carries it. Only http(s) is navigable.
function safeDocumentUrl(url) {
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.protocol === "https:" || parsed.protocol === "http:" ? parsed.href : "#";
  } catch {
    return "#";
  }
}

function formatDocumentListDisplayName(fileName) {
    if (!fileName) {
        return "N/A";
    }
    return fileName.length > 30 ? `${fileName.slice(0, 30)}...` : fileName;
}

const Documents = () => {
  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  const { enqueueSnackbar } = useSnackbar();
  const params = useParams();
  const [confirmDocumentDeletePopup, setConfirmDocumentDeletePopup] = useState(false);
  const [skyflow_id, setSkyflow_id] = useState("");
  const [showLoader, setShowLoader] = useState(false);
  const navigate = useNavigate();

  const checkBlocked = async () => {
    const userId = JSON.parse(localStorage.getItem('currentUser')).user?._id;
    try {
      // Call the API to check the user's blocked status
      const response = await checkBlockedStatus(userId);
      if (response?.response?.status === 401) {
        clearSession();
        navigate("/login");
      }
      else {
        const { isBlocked, isDeleted } = response.data;

        if (isBlocked || isDeleted) {
          // Clear the currentUser from localStorage
          clearSession();

          // Navigate to the login page
          navigate("/login");
        }
      }
    } catch (error) {
      console.error("Error checking blocked status:", error);
      // Optionally handle errors, e.g., show an error message
    }
  }
  useEffect(() => {
    checkBlocked();
    getUploadedDocuments()
  }, []); // eslint-disable-line

  const getUploadedDocuments = async () => {
    setShowLoader(true)
    const clientId = JSON.parse(window.localStorage.getItem("currentUser")).user._id;
    const data = await getDocuments(params?.clientId ? params?.clientId : clientId);

    if (data.status === 200) {
      setUploadedDocuments(data.data.data);
    } else if (data?.response?.status === 404) {
      setUploadedDocuments([])
    } else if (isForbiddenClientAccess(data)) {
      enqueueSnackbar(CLIENT_ACCESS_DENIED_MESSAGE, {
        variant: "error",
        anchorOrigin: { horizontal: "right", vertical: "top" },
      });
      navigate(CLIENT_ACCESS_DENIED_REDIRECT);
    }
    setShowLoader(false)
  }



  const cheackAndThrowError = (files) => {
    const filesArray = Array.from(files);
    let state = false;
    let alreadyUploaded = false;
    let invalidFileType = false;

    const allowedTypes = new Set([
      'application/pdf',
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/gif',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/csv',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ]);

    const allowedExtensions = new Set(['.pdf', '.jpg', '.jpeg', '.png', '.gif', '.xls', '.xlsx', '.csv', '.doc', '.docx']);

    let temp = filesArray.filter((data) => {

      if (data.size > 26214400) {
        state = true;
        return false;
      }
      else if (!allowedTypes.has(data.type) && 
               ![...allowedExtensions].some(ext => data.name.toLowerCase().endsWith(ext))) {
        invalidFileType = true;
        return false;
      }
    else if (uploadedDocuments.some(obj => obj.originalname === data.name)) {
      alreadyUploaded = true;
      return false;
    }
    else {
      return true;
    }
  });

    if (state) {
      setShowLoader(true);
      enqueueSnackbar(`${filesArray.length === 1 ? "Document size must be less than 25 MB." : "Some files are larger than 25 MB and were not added."}`, {
        variant: 'error',
        anchorOrigin: { horizontal: 'right', vertical: 'top' },
      });
      setShowLoader(false);
      state = false;
    }

    if (alreadyUploaded) {
      enqueueSnackbar(`${filesArray.length === 1 ? "Document already exists." : "Some files have already been uploaded and were not added."}`, {
        variant: 'error',
        anchorOrigin: { horizontal: 'right', vertical: 'top' },
      });
      setShowLoader(false);
    }

    if (invalidFileType) {
      enqueueSnackbar(`${filesArray.length === 1 ? "This file type is now allowed" : "Only PDF files are accepted and some files were not added."}`, {
        variant: 'error',
        anchorOrigin: { horizontal: 'right', vertical: 'top' },
      });
      setShowLoader(false);
    }

    return temp;
  };

  const handleAddFile = async (e) => {

    setShowLoader(true);
    if (e?.target?.files.length > 0) {
      let files = cheackAndThrowError(e.target.files);

      if (files.length > 0) {
        const form = new FormData();
        files.forEach(file => form.append('files', file));
        let data;
        try {
          data = await uploadDocument(form);
          if (data.status === 200) {
            enqueueSnackbar("Document uploaded! Your advisor will review it within 1 business day.", {
              variant: "success",
              anchorOrigin: { horizontal: "right", vertical: "top" },
            });
            getUploadedDocuments();
          } else {
            enqueueSnackbar("We couldn’t upload that document. Please try again or send it in a different format.", {
              variant: "error",
              anchorOrigin: { horizontal: "right", vertical: "top" },
            });
          }
        } catch (error) {
          enqueueSnackbar("We couldn’t upload that document. Please try again or send it in a different format.", {
            variant: "error",
            anchorOrigin: { horizontal: "right", vertical: "top" },
          });
        } finally {
          setShowLoader(false);
        }
      }
    }
    e.target.value = null;
  }

  const handleDelete = async () => {
    if (skyflow_id) {
      const data = await removeDocument(skyflow_id);
      if (data.status === 200) {
        // Remove the deleted document from state immediately
        setUploadedDocuments(prevDocs => prevDocs.filter(doc => doc.skyflow_id !== skyflow_id));

        enqueueSnackbar("Document deleted successfully.", {
          variant: "success",
          anchorOrigin: { horizontal: "right", vertical: "top" }
        });

        documentDeletePopupClose();
      } else {
        enqueueSnackbar(data?.data?.message || "Failed to delete document", {
          variant: "error",
          anchorOrigin: { horizontal: "right", vertical: "top" }
        });
      }
    }
  };

  const handleDocumentDeleteClick = (id) => {
    setSkyflow_id(id)
    setConfirmDocumentDeletePopup(true)
  };

  const documentDeletePopupClose = () => {
    setSkyflow_id("")
    setConfirmDocumentDeletePopup(false);
  };

  // Find the document currently selected for deletion
  const selectedDocument = uploadedDocuments.find(
    (doc) => doc.skyflow_id === skyflow_id
  );
  const isChatDocument = selectedDocument?.uploaded_from === "chat";

  const userType = JSON.parse(localStorage.getItem("currentUser"))?.user?.userType;
  const isStaff =
    userType === "advisor" || userType === "super-admin" || userType === "admin";
  const isClient = userType === "client";

  return (
    <Page>
      {isStaff && (
        <DashboardUserHeader clientId={params?.clientId ? params?.clientId : null} />
      )}

      {!isStaff && (
        <PageHead
          title="Documents"
          subtitle="Upload any documents you’d like your advisor to review. You can add pay stubs, tax returns, loan statements, insurance policies, retirement account statements, or anything else that’s helpful."
          tools={
            isClient ? (
              <Button as="label" className="tw-cursor-pointer">
                <StarsIcon name="upload" className="tw-h-5 tw-w-5 tw-shrink-0" />
                Add Document{" "}
                <input
                  type="file"
                  onChange={handleAddFile}
                  accept=".pdf,.jpg,.jpeg,.png,.gif,.xls,.xlsx,.csv,.doc,.docx"
                  className="tw-sr-only"
                  multiple
                />
              </Button>
            ) : null
          }
        />
      )}

      {/* Pooled inbox SP3: the client's own signed agreements and delivered
          disclosures, above the uploads. Staff views of a client do not show it. */}
      {isClient && !params?.clientId && <AgreementsSection />}

      {showLoader && <Loader />}
      {!showLoader && uploadedDocuments.length > 0 && (
        <TableWrap data-slot="client-document-list">
          <Table>
            <THead>
              <tr>
                <Th><span className="tw-sr-only">Document</span></Th>
                <Th hideOnPhone><span className="tw-sr-only">Uploaded on</span></Th>
                {isClient && <Th align="right"><span className="tw-sr-only">Actions</span></Th>}
              </tr>
            </THead>
            <TBody>
              {uploadedDocuments.map((data) => {
                const fileName = data?.originalname || "";
                const displayFileName = formatDocumentListDisplayName(fileName);
                const href = safeDocumentUrl(data?.file_url);
                const fileKind = /\.(png|jpe?g|webp|avif|gif)$/i.test(fileName) ? "image" : "file";
                return (
                  <Tr key={data?.skyflow_id} data-slot="signle-client-document">
                    <Td>
                      <div className="tw-flex tw-items-center tw-gap-3">
                        <StarsIcon
                          name={fileKind === "image" ? "image" : "file"}
                          className="tw-h-5 tw-w-5 tw-shrink-0 tw-text-text-accent"
                        />
                        <div className="tw-min-w-0">
                          {/* SEC-037: a plain <a>, not react-router's <Link> — this
                              href is server-supplied, not an in-app route, and a
                              vulnerable react-router version treats a
                              protocol-relative `to` as an open redirect. */}
                          <a
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="tw-text-link tw-no-underline"
                          >
                            <h5 title={fileName} className="tw-m-0 tw-text-sm tw-font-semibold">
                              {displayFileName}
                            </h5>
                          </a>
                          <p className="tw-m-0 tw-text-xs tw-text-text-muted rail:tw-hidden">
                            Uploaded on : {moment(data?.updatedAt).format("LLL")}
                          </p>
                        </div>
                      </div>
                    </Td>
                    <Td hideOnPhone>
                      Uploaded on : {moment(data?.updatedAt).format("LLL")}
                    </Td>
                    {isClient && (
                      <Td align="right">
                        <div className="tw-flex tw-items-center tw-justify-end tw-gap-2">
                          <Button
                            as="a"
                            variant="icon"
                            size="sm"
                            iconOnly
                            data-slot="doc-preview"
                            aria-label="View document"
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <StarsIcon name="eye" className="tw-h-4 tw-w-4 tw-shrink-0" />
                          </Button>
                          <Button
                            variant="icon"
                            size="sm"
                            iconOnly
                            aria-label="Delete document"
                            onClick={() => handleDocumentDeleteClick(data?.skyflow_id)}
                          >
                            <StarsIcon name="trash" className="tw-h-4 tw-w-4 tw-shrink-0" />
                          </Button>
                        </div>
                      </Td>
                    )}
                  </Tr>
                );
              })}
            </TBody>
          </Table>
        </TableWrap>
      )}
      {!showLoader && uploadedDocuments.length === 0 && (
        <div className="tw-text-center">
          <img src={documentPlaceholder} alt="no document" className="tw-mx-auto" />
          <p className="tw-mt-3 tw-text-text">You haven’t uploaded any documents yet. <br></br>Your advisor may request documents as needed — or you can upload something anytime.</p>
        </div>
      )}

      {/* confirm document delete popup */}
      <Dialog
        open={confirmDocumentDeletePopup}
        onOpenChange={(o) => !o && documentDeletePopupClose()}
      >
        <DialogContent
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <DialogTitle className="tw-sr-only">Delete document</DialogTitle>
          <DialogDescription className="tw-text-center tw-text-md">
            You are attempting to delete this document.
          </DialogDescription>
          {isChatDocument && (
            <p className="tw-mb-1 tw-text-center tw-text-sm tw-text-text-muted">
              This document is also available in your chat history and will be affected.
            </p>
          )}
          <p className="tw-m-0 tw-text-center tw-text-md tw-font-bold tw-text-text">Are you sure?</p>
          <DialogActions className="tw-justify-center">
            <Button
              variant="icon"
              iconOnly
              aria-label="Close"
              onClick={documentDeletePopupClose}
            >
              <StarsIcon name="x" className="tw-h-5 tw-w-5 tw-shrink-0" />
            </Button>
            <Button onClick={handleDelete}>
              <StarsIcon name="check" className="tw-h-4 tw-w-4 tw-shrink-0" />
              Confirm
            </Button>
          </DialogActions>
        </DialogContent>
      </Dialog>
    </Page>
  );
};

export default Documents;
